// ────────────────────────────────────────────────────────────────────────────
// Dex sprite store.
//
// Holds per-species sprite crops captured during Box Scan / Detail OCR.
// The store is in-memory only (sandbox iframe forbids localStorage), so
// crops live for the session and are recaptured on next scan.
//
// Per-species dedupe rule:
//   The crop attached to the highest-CP entry seen in this session wins,
//   so the Dex thumbnail is always the strongest specimen the user owns.
// ────────────────────────────────────────────────────────────────────────────
import { createContext, useCallback, useContext, useMemo, useRef, useState, ReactNode } from "react";

interface SpriteEntry {
  /** Cropped sprite, ready as <img src="data:..."/>. */
  dataUrl: string;
  /** CP of the specimen the crop came from. Higher = better thumbnail. */
  cp: number;
  /** When the crop was taken (ms epoch). */
  capturedAt: number;
}

interface DexSpriteState {
  /** Get the best sprite we have for a species, or null. */
  spriteFor: (species: string) => string | null;
  /** Submit a candidate crop. Stored only if its CP beats the current best. */
  ingestSprite: (input: { species: string; cp: number | null; dataUrl: string | null }) => void;
  /** Total species we have a crop for in this session. */
  count: number;
}

const DexSpriteContext = createContext<DexSpriteState | null>(null);

function normaliseKey(species: string): string {
  return species.trim().toLowerCase();
}

export function DexSpriteProvider({ children }: { children: ReactNode }) {
  // Use a ref for the source of truth to avoid re-renders on every ingest;
  // bump the counter only when the species set actually grows.
  const mapRef = useRef<Map<string, SpriteEntry>>(new Map());
  const [count, setCount] = useState(0);

  const ingestSprite = useCallback(
    ({ species, cp, dataUrl }: { species: string; cp: number | null; dataUrl: string | null }) => {
      if (!species || !dataUrl) return;
      const key = normaliseKey(species);
      if (!key) return;
      const cpVal = typeof cp === "number" && Number.isFinite(cp) ? cp : 0;
      const existing = mapRef.current.get(key);
      if (!existing || cpVal >= existing.cp) {
        const isNew = !existing;
        mapRef.current.set(key, { dataUrl, cp: cpVal, capturedAt: Date.now() });
        if (isNew) setCount((n) => n + 1);
      }
    },
    [],
  );

  const spriteFor = useCallback((species: string): string | null => {
    if (!species) return null;
    const e = mapRef.current.get(normaliseKey(species));
    return e ? e.dataUrl : null;
  }, []);

  const value = useMemo<DexSpriteState>(
    () => ({ ingestSprite, spriteFor, count }),
    [ingestSprite, spriteFor, count],
  );

  return <DexSpriteContext.Provider value={value}>{children}</DexSpriteContext.Provider>;
}

export function useDexSprites(): DexSpriteState {
  const ctx = useContext(DexSpriteContext);
  if (!ctx) {
    // Fallback no-op so components can mount even in tests / older shells.
    return {
      spriteFor: () => null,
      ingestSprite: () => {},
      count: 0,
    };
  }
  return ctx;
}
